import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { endOfDay, endOfMonth, endOfWeek, format, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AccountFilterComponent } from '../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateInterval } from '../../../../_shared/enums';
import { isEmptyObject } from '../../../../_shared/helpers/object.helper';
import { AccountFilterData, DateIntervalFilterData } from '../../../../_shared/models';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { SharedCalendarComponent } from '../../../_shared/components/calendar/calendar.component';
import {
  CalendarConfig,
  CalendarDay,
  CalendarEvent,
  CalendarFilterOption,
  CalendarFilterSection,
  CalendarTodayEvent
} from '../../../_shared/models/calendar.model';
import { COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS, ComplianceCalendarEventResponse, ComplianceCalendarEventsByDateApiRequest, ComplianceCalendarEventsPreviewApiRequest, ComplianceCalendarEventType, ComplianceCalendarStatus, CompliancePhase, ComplianceOverviewRequirementsRequest, ComplianceTaskType, TodayComplianceCalendarEventResponse, TodayComplianceCalendarEventsApiRequest } from '../models/compliance-item.model';
import { ComplianceOverviewService } from '../services/compliance-overview.service';
import { ComplianceStateService } from '../../_shared/services/compliance-state.service';
import { ComplianceEventScheduleModalComponent } from './modals/compliance-event-schedule-modal/compliance-event-schedule-modal.component';
import { translate, TranslocoService } from '@jsverse/transloco';

type ComplianceCalendarEvent = CalendarEvent & {
  sourceTaskId: number;
  complianceName: string;
  taskType: ComplianceTaskType;
  phase: CompliancePhase | null;
  calendarEventType: ComplianceCalendarEventType;
};

function complianceStatusFilterOptions(): ReadonlyArray<{
  key: ComplianceCalendarStatus;
  label: string;
  color: string;
  checked: boolean;
}> {
  return [
    { key: 'Compliant', label: translate('compliance.overview.calendar.statuses.compliant'), color: '#4CAF50', checked: true },
    { key: 'Non-Compliant', label: translate('compliance.overview.calendar.statuses.nonCompliant'), color: '#FF7F00', checked: true },
    { key: 'In Progress', label: translate('compliance.overview.calendar.statuses.inProgress'), color: '#F4BB2A', checked: true },
    { key: 'Missed', label: translate('compliance.overview.calendar.statuses.missed'), color: '#6B6D71', checked: true }
  ];
}

function calendarEventTypeFilterOptions(): ReadonlyArray<{
  key: ComplianceCalendarEventType;
  label: string;
  color: string;
  checked: boolean;
  textColor?: string;
}> {
  const label = (type: ComplianceCalendarEventType) => {
    const key = COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS[type];
    return key ? translate(key) : '';
  };

  return [
    { key: 'pre-trip', label: label('pre-trip'), color: '#A9C957', checked: true },
    { key: 'post-trip', label: label('post-trip'), color: '#E6D85C', checked: true },
    { key: 'pre-work-order', label: label('pre-work-order'), color: '#67B3D8', checked: true },
    { key: 'post-work-order', label: label('post-work-order'), color: '#D9B7E8', checked: true },
    { key: 'periodic', label: label('periodic'), color: '#9254C8', checked: true, textColor: '#FFFFFF' }
  ];
}

@Component({
  selector: 'app-compliance-calendar-overview',
  standalone: true,
  imports: [CommonModule, SharedCalendarComponent, AccountFilterComponent],
  templateUrl: './compliance-calendar-overview.component.html',
  styleUrl: './compliance-calendar-overview.component.scss'
})
export class ComplianceCalendarOverviewComponent implements OnInit, OnDestroy {
  private readonly complianceOverviewService = inject(ComplianceOverviewService);
  private readonly complianceOverviewStateService = inject(ComplianceStateService);
  private readonly errorService = inject(ErrorHandlerService);
  private readonly modalService = inject(NgbModal);
  private readonly toastr = inject(ToastrService);
  // Declared before `calendarConfig`, whose initialiser translates its labels.
  private readonly transloco = inject(TranslocoService);
  private readonly destroy$ = new Subject<void>();
  private readonly mainViewCancel$ = new Subject<void>();
  private readonly todayCancel$ = new Subject<void>();
  private readonly dayExpandCancel$ = new Subject<void>();
  private readonly eventScheduleCancel$ = new Subject<void>();
  private readonly dragDropCancel$ = new Subject<void>();
  private requestSubscription?: Subscription;

  calendarConfig: CalendarConfig = {
    title: this.transloco.translate('compliance.overview.calendar.title'),
    backLink: '/asset-management/compliance-overview',
    searchPlaceholder: this.transloco.translate('compliance.overview.calendar.searchPlaceholder'),
    filterSectionTitle: this.transloco.translate('compliance.overview.calendar.filterSectionTitle'),
    filterOptions: complianceStatusFilterOptions().map(option => ({ ...option })),
    filterSections: [
      {
        key: 'calendar-event-types',
        title: this.transloco.translate('compliance.overview.calendar.complianceTypes'),
        options: calendarEventTypeFilterOptions().map(option => ({ ...option }))
      }
    ],
    todaySectionTitle: this.transloco.translate('compliance.overview.calendar.todaySectionTitle'),
    showTodaySection: true
  };

  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  accountFilter: AccountFilterData = { accountIds: [] };
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  isLoadingAllEvents: boolean = true;
  isLoadingTodayEvents: boolean = true;
  expandedDayLoadingKey: string | null = null;
  previewEvents: ComplianceCalendarEvent[] = [];
  expandedDayEvents: ComplianceCalendarEvent[] = [];
  expandedDayKey: string | null = null;
  todayEvents: CalendarTodayEvent[] = [];
  todayPage: number = 1;
  todayTotalPages: number = 0;
  isEventScheduleModalOpen: boolean = false;
  isUpdatingDraggedEvent: boolean = false;

  private filterRequest: ComplianceOverviewRequirementsRequest = {
    accountIds: [],
    page: 1,
    pageSize: 10
  };
  private searchTerm: string = '';
  private currentDate: Date = new Date();
  private skipInitialDateFilterReload: boolean = true;
  private skipInitialAccountFilterReload: boolean = true;

  async ngOnInit(): Promise<void> {
    this.transloco.selectTranslation().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshCalendarLabels();
    });
    const currentRequest = this.complianceOverviewStateService.getCurrentRequest();
    if (currentRequest) {
      this.filterRequest = {
        ...this.filterRequest,
        ...currentRequest,
        accountIds: currentRequest.accountIds ?? []
      };
      this.accountFilter = { accountIds: this.filterRequest.accountIds };
    }

    this.requestSubscription = this.complianceOverviewStateService.request$.subscribe(request => {
      this.filterRequest = {
        ...this.filterRequest,
        ...request,
        accountIds: request.accountIds ?? []
      };
      this.accountFilter = { accountIds: this.filterRequest.accountIds };
      void this.reloadCalendarData();
    });

    if (!currentRequest) {
      await this.reloadCalendarData();
    }
  }

  private refreshCalendarLabels(): void {
    const translate = (key: string) => this.transloco.translate(key);
    const statusLabels: Record<string, string> = {
      'Compliant': translate('compliance.overview.calendar.statuses.compliant'),
      'Non-Compliant': translate('compliance.overview.calendar.statuses.nonCompliant'),
      'In Progress': translate('compliance.overview.calendar.statuses.inProgress'),
      'Missed': translate('compliance.overview.calendar.statuses.missed'),
    };
    this.calendarConfig = {
      ...this.calendarConfig,
      title: translate('compliance.overview.calendar.title'),
      searchPlaceholder: translate('compliance.overview.calendar.searchPlaceholder'),
      filterSectionTitle: translate('compliance.overview.calendar.filterSectionTitle'),
      filterOptions: this.calendarConfig.filterOptions.map(option => ({ ...option, label: statusLabels[option.key] ?? option.label })),
      filterSections: this.calendarConfig.filterSections?.map(section => ({
        ...section,
        title: section.key === 'calendar-event-types' ? translate('compliance.overview.calendar.complianceTypes') : section.title,
        options: section.options.map(option => {
          const key = COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS[option.key as ComplianceCalendarEventType];
          return { ...option, label: key ? translate(key) : option.label };
        }),
      })),
      todaySectionTitle: translate('compliance.overview.calendar.todaySectionTitle'),
    };
  }

  ngOnDestroy(): void {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }

    [this.mainViewCancel$, this.todayCancel$, this.dayExpandCancel$, this.eventScheduleCancel$, this.dragDropCancel$, this.destroy$]
      .forEach(s => { s.next(); s.complete(); });
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    if (isEmptyObject(filter)) {
      return;
    }

    this.accountFilter = filter;

    if (this.skipInitialAccountFilterReload) {
      this.skipInitialAccountFilterReload = false;
      return;
    }

    this.filterRequest = { ...this.filterRequest, accountIds: filter.accountIds ?? [] };
    this.complianceOverviewStateService.updateComplianceRequest({ ...this.filterRequest });
  }

  onFilterChange(options: CalendarFilterOption[]): void {
    this.calendarConfig.filterOptions = options;
    this.clearExpandedDayEvents();
    void this.reloadCalendarData();
  }

  onFilterSectionsChange(sections: CalendarFilterSection[]): void {
    this.calendarConfig.filterSections = sections;
    this.clearExpandedDayEvents();
    void this.reloadCalendarData();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.clearExpandedDayEvents();
    void this.reloadCalendarData();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;

    if (this.skipInitialDateFilterReload) {
      this.skipInitialDateFilterReload = false;
      return;
    }

    void this.resetMonthCalendarEventsState();
  }

  async onDateChange(dates: { currentDate: Date; selectedDate: Date }): Promise<void> {
    this.currentDate = dates.currentDate;
    const pivot = dates.selectedDate ?? this.currentDate;

    switch (this.dateFilter.interval) {
      case DateInterval.Day:
        this.dateFilter.startDate = startOfDay(pivot);
        this.dateFilter.endDate = endOfDay(pivot);
        break;
      case DateInterval.Week:
        this.dateFilter.startDate = startOfWeek(pivot, { weekStartsOn: 0 });
        this.dateFilter.endDate = endOfWeek(pivot, { weekStartsOn: 0 });
        break;
      case DateInterval.Month:
      default:
        this.dateFilter.startDate = startOfMonth(this.currentDate);
        this.dateFilter.endDate = endOfMonth(this.currentDate);
        break;
    }

    await this.resetMonthCalendarEventsState();
  }

  async onEventClick(event: CalendarEvent): Promise<void> {
    if (this.isEventScheduleModalOpen) {
      return;
    }

    const complianceEvent = event as ComplianceCalendarEvent;
    this.isEventScheduleModalOpen = true;

    try {
      const response = await this.complianceOverviewService.getComplianceCalendarEventScheduleAsync(
        {
          taskType: complianceEvent.taskType,
          sourceTaskId: complianceEvent.sourceTaskId,
          phase: complianceEvent.phase
        },
        this.eventScheduleCancel$
      );

      if (!response.data) {
        this.isEventScheduleModalOpen = false;
        return;
      }

      const modalRef = this.modalService.open(ComplianceEventScheduleModalComponent, {
        centered: true,
        backdrop: 'static',
        windowClass: 'compliance-event-schedule-modal-window'
      });

      modalRef.componentInstance.sourceTaskId = complianceEvent.sourceTaskId;
      modalRef.componentInstance.taskType = complianceEvent.taskType;
      modalRef.componentInstance.phase = complianceEvent.phase;
      modalRef.componentInstance.eventData = response.data;

      modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe(result => {
        this.isEventScheduleModalOpen = false;
        if (result === true) {
          void this.reloadCalendarData();
        }
      });

      modalRef.dismissed.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isEventScheduleModalOpen = false;
      });
    } catch (error) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isEventScheduleModalOpen = false;
        }
      });
    }
  }

  onTodayScrollDown(): void {
    if (this.isLoadingTodayEvents || this.todayPage >= this.todayTotalPages) {
      return;
    }

    this.todayPage++;
    void this.loadTodayCalendarEventsData();
  }

  onMonthDayExpand(day: CalendarDay): void {
    const dayKey = this.getDayKey(day.date);
    if (this.expandedDayLoadingKey === dayKey) {
      return;
    }

    void this.loadCalendarEventsByDateData(day.date);
  }

  async onEventDrop(data: { event: CalendarEvent; targetDay: CalendarDay }): Promise<void> {
    if (this.isUpdatingDraggedEvent) {
      return;
    }

    const complianceEvent = data.event as ComplianceCalendarEvent;
    const originalScheduledDate = new Date(complianceEvent.scheduledDate);
    const scheduledDate = this.mergeTargetDateWithEventTime(data.targetDay.date, originalScheduledDate);

    if (scheduledDate < new Date()) {
      this.toastr.warning(this.transloco.translate('compliance.overview.calendar.toasts.pastDate'));
      return;
    }

    this.isUpdatingDraggedEvent = true;
    const previousPreviewEvents = this.cloneCalendarEvents(this.previewEvents);

    try {
      if (data.targetDay.isCurrentMonth) {
        this.applyOptimisticEventDrop(complianceEvent.id, scheduledDate);
      } else {
        this.previewEvents = this.previewEvents.filter(event => event.id !== complianceEvent.id);
      }

      await this.complianceOverviewService.updateComplianceCalendarEventScheduleAsync(
        {
          taskType: complianceEvent.taskType,
          sourceTaskId: complianceEvent.sourceTaskId,
          scheduledDate,
          phase: complianceEvent.phase
        },
        this.dragDropCancel$
      );

      await this.reloadCalendarData();
      this.isUpdatingDraggedEvent = false;
    } catch (error) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.previewEvents = previousPreviewEvents;
          this.isUpdatingDraggedEvent = false;
        }
      });
    }
  }

  private async reloadCalendarData(): Promise<void> {
    await Promise.all([
      this.resetMonthCalendarEventsState(),
      this.resetTodayCalendarEventsState()
    ]);
  }

  private async resetTodayCalendarEventsState(showLoader: boolean = true): Promise<void> {
    this.todayCancel$.next();
    this.todayPage = 1;
    this.todayTotalPages = 0;
    this.todayEvents = [];

    await this.loadTodayCalendarEventsData(showLoader);
  }

  private async resetMonthCalendarEventsState(showLoader: boolean = true): Promise<void> {
    this.mainViewCancel$.next();
    this.dayExpandCancel$.next();
    this.expandedDayLoadingKey = null;
    this.clearExpandedDayEvents();
    this.previewEvents = [];

    await this.loadCalendarEventsForCurrentView(showLoader);
  }

  private async loadCalendarEventsForCurrentView(showLoader: boolean = true): Promise<void> {
    if (this.dateFilter.interval === DateInterval.Month || this.dateFilter.interval == null) {
      await this.loadCalendarEventsPreviewData(showLoader);
    } else {
      await this.loadAllCalendarEventsForRange(showLoader);
    }
  }

  private async loadTodayCalendarEventsData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingTodayEvents = true;
    }

    if (!this.hasSelectedCalendarFilters()) {
      this.todayEvents = [];
      this.todayTotalPages = 0;
      this.isLoadingTodayEvents = false;
      return;
    }

    try {
      const response = await this.complianceOverviewService.getTodayComplianceCalendarEventsAsync(
        this.createTodayCalendarRequest(),
        this.todayCancel$
      );

      const todayCalendarEvents = response.data?.results ?? [];
      const newTodayEvents = todayCalendarEvents.map(event => this.mapTodayEvent(event));

      this.todayEvents = this.todayPage === 1
        ? newTodayEvents
        : [...this.todayEvents, ...newTodayEvents];
      this.todayTotalPages = response.data?.totalPages ?? 0;
      this.isLoadingTodayEvents = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingTodayEvents = false;
        }
      });
    }
  }

  private async loadCalendarEventsPreviewData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    if (!this.hasSelectedCalendarFilters()) {
      this.previewEvents = [];
      this.isLoadingAllEvents = false;
      return;
    }

    try {
      const response = await this.complianceOverviewService.getComplianceCalendarEventsPreviewAsync(
        this.createCalendarEventsPreviewRequest(),
        this.mainViewCancel$
      );

      this.previewEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      this.isLoadingAllEvents = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingAllEvents = false;
        }
      });
    }
  }

  private async loadCalendarEventsByDateData(date: Date): Promise<void> {
    this.dayExpandCancel$.next();
    const dayKey = this.getDayKey(date);
    this.expandedDayLoadingKey = dayKey;
    this.expandedDayKey = dayKey;
    this.expandedDayEvents = [];

    if (!this.hasSelectedCalendarFilters()) {
      this.expandedDayLoadingKey = null;
      return;
    }

    try {
      const response = await this.complianceOverviewService.getComplianceCalendarEventsByDateAsync(
        this.createCalendarEventsByDateRequest(date),
        this.dayExpandCancel$
      );

      this.expandedDayEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      this.expandedDayLoadingKey = null;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.expandedDayLoadingKey = null;
        }
      });
    }
  }

  private async loadAllCalendarEventsForRange(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    if (!this.hasSelectedCalendarFilters()) {
      this.previewEvents = [];
      this.isLoadingAllEvents = false;
      return;
    }

    try {
      const { timeFrom, timeTo } = this.getCalendarDateRange();
      const request: ComplianceCalendarEventsByDateApiRequest = {
        ...this.getCalendarFilterRequest(timeFrom, timeTo),
        date: timeFrom,
        endOfDay: timeTo
      };

      const response = await this.complianceOverviewService.getComplianceCalendarEventsByDateAsync(
        request,
        this.mainViewCancel$
      );

      this.previewEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      this.isLoadingAllEvents = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingAllEvents = false;
        }
      });
    }
  }

  private createCalendarEventsPreviewRequest(): ComplianceCalendarEventsPreviewApiRequest {
    const { timeFrom, timeTo } = this.getCalendarDateRange();

    return this.getCalendarFilterRequest(timeFrom, timeTo);
  }

  private createCalendarEventsByDateRequest(date: Date): ComplianceCalendarEventsByDateApiRequest {
    const { timeFrom, timeTo } = this.getCalendarDateRange();

    return {
      ...this.getCalendarFilterRequest(timeFrom, timeTo),
      date,
      endOfDay: endOfDay(date)
    };
  }

  private createTodayCalendarRequest(): TodayComplianceCalendarEventsApiRequest {
    return {
      ...this.getCalendarFilterRequest(startOfDay(new Date()), endOfDay(new Date())),
      page: this.todayPage,
      pageSize: 20
    };
  }

  private getCalendarFilterRequest(timeFrom: Date, timeTo: Date) {
    return {
      accountIds: this.filterRequest.accountIds ?? [],
      timeFrom,
      timeTo,
      statuses: this.getSelectedStatuses(),
      calendarEventTypes: this.getSelectedCalendarEventTypes(),
      searchTerm: this.searchTerm,
      complianceId: this.filterRequest.complianceId ?? null,
      regionId: this.filterRequest.complianceRegion?.type === 'region' ? this.filterRequest.complianceRegion.id : null,
      countryId: this.filterRequest.complianceRegion?.type === 'country' ? this.filterRequest.complianceRegion.id : null
    };
  }

  private getCalendarDateRange(): { timeFrom: Date; timeTo: Date } {
    return {
      timeFrom: this.dateFilter.startDate ?? startOfMonth(this.currentDate),
      timeTo: this.dateFilter.endDate ?? endOfMonth(this.currentDate)
    };
  }

  private getSelectedStatuses(): ComplianceCalendarStatus[] {
    return this.calendarConfig.filterOptions
      .filter(option => option.checked)
      .map(option => option.key as ComplianceCalendarStatus);
  }

  private getSelectedCalendarEventTypes(): ComplianceCalendarEventType[] {
    const selectedTypes = (this.calendarConfig.filterSections ?? [])
      .flatMap(section => section.options)
      .filter(option => option.checked)
      .map(option => option.key as ComplianceCalendarEventType);

    const knownTypes = calendarEventTypeFilterOptions().map(option => option.key);
    const allKnownTypesSelected = knownTypes.every(type => selectedTypes.includes(type));

    return allKnownTypesSelected
      ? [...selectedTypes, 'unknown']
      : selectedTypes;
  }

  private hasSelectedCalendarFilters(): boolean {
    return this.getSelectedStatuses().length > 0 && this.getSelectedCalendarEventTypes().length > 0;
  }

  private mapCalendarEvent(event: ComplianceCalendarEventResponse): ComplianceCalendarEvent {
    const scheduledDate = new Date(event.scheduledDate);

    return {
      id: event.id,
      sourceTaskId: event.sourceTaskId,
      taskType: event.taskType,
      phase: event.phase ?? null,
      calendarEventType: event.calendarEventType,
      assetId: event.assetId,
      assetName: event.assetName,
      complianceName: event.complianceName,
      colorKey: event.calendarEventType,
      type: this.normalizeCalendarStatus(event.type),
      startTime: this.formatTime(scheduledDate),
      scheduledDate,
      isMissedEvent: event.isMissedEvent
    };
  }

  private mapTodayEvent(event: TodayComplianceCalendarEventResponse): CalendarTodayEvent {
    const startTime = new Date(event.startTime);

    return {
      id: event.id,
      assetId: event.assetId,
      assetName: event.assetName,
      colorKey: event.calendarEventType,
      type: this.normalizeCalendarStatus(event.type),
      startTime: this.formatTime(startTime),
      endTime: null,
      workshop: event.complianceName
    };
  }

  private normalizeCalendarStatus(status: string): ComplianceCalendarStatus {
    switch (status.trim().toLowerCase()) {
      case 'compliant':
        return 'Compliant';
      case 'in progress':
      case 'in-progress':
        return 'In Progress';
      case 'missed':
      case 'overdue':
        return 'Missed';
      case 'non compliant':
      case 'non-compliant':
        return 'Non-Compliant';
      default:
        console.warn(`Unknown compliance calendar status: ${status}`);
        return 'Non-Compliant';
    }
  }

  private clearExpandedDayEvents(): void {
    this.expandedDayKey = null;
    this.expandedDayEvents = [];
  }

  private cloneCalendarEvents(events: ComplianceCalendarEvent[]): ComplianceCalendarEvent[] {
    return events.map(event => ({
      ...event,
      scheduledDate: new Date(event.scheduledDate)
    }));
  }

  private applyOptimisticEventDrop(eventId: number, scheduledDate: Date): void {
    this.previewEvents = this.previewEvents.map(event => event.id === eventId
      ? {
        ...event,
        scheduledDate,
        startTime: this.formatTime(scheduledDate)
      }
      : event);
  }

  private mergeTargetDateWithEventTime(targetDate: Date, originalDate: Date): Date {
    const scheduledDate = new Date(targetDate);
    scheduledDate.setHours(
      originalDate.getHours(),
      originalDate.getMinutes(),
      originalDate.getSeconds(),
      originalDate.getMilliseconds()
    );

    return scheduledDate;
  }

  private formatTime(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private getDayKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }
}